import { createFileRoute, redirect, Link } from "@tanstack/react-router";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { LicenseStatusCard } from "@/components/subscription/LicenseStatusCard";
import {
  TrendingUp,
  TrendingDown,
  FileText,
  Users,
  Package,
  DollarSign,
  AlertCircle,
  ArrowRight,
  Plus,
  Clock,
  CheckCircle2,
  LayoutGrid,
  CreditCard,
} from "lucide-react";
import {
  useDashboardStats,
  useRecentInvoices,
  useTopClients,
  useRevenueChart,
} from "@/hooks/queries/useDashboard";
import { useNavigate } from "@tanstack/react-router";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/dashboard/")({
  beforeLoad: ({ context }) => {
    if (!context.auth.isAuthenticated) {
      throw redirect({ to: "/login" });
    }
  },
  component: DashboardPage,
});

function DashboardPage() {
  const navigate = useNavigate();
  const { data: stats, isLoading: statsLoading } = useDashboardStats();
  const { data: recentInvoices } = useRecentInvoices(5);
  const { data: topClients } = useTopClients(5);
  const { data: revenueChart } = useRevenueChart(6);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("ar-DZ", {
      style: "currency",
      currency: "DZD",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const statusColors = {
    draft: "bg-orange-100 text-orange-800",
    sent: "bg-blue-100 text-blue-800",
    partial: "bg-yellow-100 text-yellow-800",
    paid: "bg-green-100 text-green-800",
    overdue: "bg-red-100 text-red-800",
  };

  const statusLabels = {
    draft: "مسودة / Brouillon",
    sent: "مرسلة / Envoyée",
    partial: "جزئي / Partiel",
    paid: "مدفوعة / Payée",
    overdue: "متأخرة / En retard",
  };

  if (statsLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              لوحة القيادة / Tableau de Bord
            </h1>
            <p className="text-gray-500 mt-1">
              نظرة عامة على أعمالك / Vue d'ensemble de votre activité
            </p>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => navigate({ to: "/draft-invoice" })}>
              <Plus className="w-4 h-4 mr-2" />
              فاتورة جديدة / Nouvelle facture
            </Button>
          </div>
        </div>

        {/* License Status Card - Prominent placement */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1">
            <LicenseStatusCard />
          </div>
          <div className="lg:col-span-2">
            {/* Quick Stats Row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 border-blue-200">
                <CardContent className="p-4">
                  <div className="text-2xl font-bold text-blue-700">{stats?.invoices.total || 0}</div>
                  <div className="text-sm text-blue-600">Factures ce mois</div>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 border-green-200">
                <CardContent className="p-4">
                  <div className="text-2xl font-bold text-green-700">{formatCurrency(stats?.revenue.thisMonth || 0)}</div>
                  <div className="text-sm text-green-600">Revenu</div>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 border-purple-200">
                <CardContent className="p-4">
                  <div className="text-2xl font-bold text-purple-700">{stats?.clients.total || 0}</div>
                  <div className="text-sm text-purple-600">Clients</div>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-900/20 dark:to-orange-800/20 border-orange-200">
                <CardContent className="p-4">
                  <div className="text-2xl font-bold text-orange-700">{stats?.products.total || 0}</div>
                  <div className="text-sm text-orange-600">Produits</div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Revenue Card */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">
                الإيرادات / Revenu
              </CardTitle>
              <DollarSign className="w-5 h-5 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-gray-900">
                {formatCurrency(stats?.revenue.thisMonth || 0)}
              </div>
              <div className="flex items-center mt-2 text-sm">
                {stats && stats.revenue.percentageChange >= 0 ? (
                  <>
                    <TrendingUp className="w-4 h-4 text-green-600 mr-1" />
                    <span className="text-green-600 font-medium">
                      +{stats.revenue.percentageChange.toFixed(1)}%
                    </span>
                  </>
                ) : (
                  <>
                    <TrendingDown className="w-4 h-4 text-red-600 mr-1" />
                    <span className="text-red-600 font-medium">
                      {stats?.revenue.percentageChange.toFixed(1)}%
                    </span>
                  </>
                )}
                <span className="text-gray-500 ml-2">vs mois dernier</span>
              </div>
            </CardContent>
          </Card>

          {/* Invoices Card */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">
                الفواتير / Factures
              </CardTitle>
              <FileText className="w-5 h-5 text-blue-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-gray-900">
                {stats?.invoices.total || 0}
              </div>
              <div className="flex items-center gap-4 mt-2 text-xs">
                <div className="flex items-center">
                  <CheckCircle2 className="w-3 h-3 text-green-600 mr-1" />
                  <span className="text-gray-600">
                    {stats?.invoices.paid || 0} payées
                  </span>
                </div>
                <div className="flex items-center">
                  <Clock className="w-3 h-3 text-yellow-600 mr-1" />
                  <span className="text-gray-600">
                    {stats?.invoices.pending || 0} en cours
                  </span>
                </div>
                {(stats?.invoices.overdue || 0) > 0 && (
                  <div className="flex items-center">
                    <AlertCircle className="w-3 h-3 text-red-600 mr-1" />
                    <span className="text-red-600 font-medium">
                      {stats?.invoices.overdue} en retard
                    </span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Clients Card */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">
                العملاء / Clients
              </CardTitle>
              <Users className="w-5 h-5 text-purple-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-gray-900">
                {stats?.clients.total || 0}
              </div>
              <div className="flex items-center gap-4 mt-2 text-xs">
                <div className="flex items-center">
                  <div className="w-2 h-2 rounded-full bg-green-500 mr-1" />
                  <span className="text-gray-600">
                    {stats?.clients.active || 0} actifs
                  </span>
                </div>
                {(stats?.clients.new || 0) > 0 && (
                  <div className="flex items-center">
                    <Plus className="w-3 h-3 text-blue-600 mr-1" />
                    <span className="text-blue-600 font-medium">
                      {stats?.clients.new} nouveaux
                    </span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Products Card */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">
                المنتجات / Produits
              </CardTitle>
              <Package className="w-5 h-5 text-orange-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-gray-900">
                {stats?.products.total || 0}
              </div>
              <div className="mt-2 text-xs">
                {(stats?.products.lowStock || 0) > 0 ? (
                  <div className="flex items-center">
                    <AlertCircle className="w-3 h-3 text-red-600 mr-1" />
                    <span className="text-red-600 font-medium">
                      {stats?.products.lowStock} stock faible
                    </span>
                  </div>
                ) : (
                  <span className="text-green-600">Tous en stock</span>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Charts & Lists */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Revenue Chart */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="text-lg font-semibold">
                تطور الإيرادات / Évolution du Revenu
              </CardTitle>
            </CardHeader>
            <CardContent>
              {revenueChart && revenueChart.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={revenueChart}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis
                      dataKey="month"
                      stroke="#888"
                      style={{ fontSize: "12px" }}
                    />
                    <YAxis
                      stroke="#888"
                      style={{ fontSize: "12px" }}
                      tickFormatter={(value) => `${(value / 1000).toFixed(0)}K`}
                    />
                    <Tooltip
                      formatter={(value: number) => formatCurrency(value)}
                      contentStyle={{
                        backgroundColor: "#fff",
                        border: "1px solid #e5e7eb",
                        borderRadius: "8px",
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="revenue"
                      stroke="#10b981"
                      strokeWidth={3}
                      dot={{ fill: "#10b981", r: 4 }}
                      activeDot={{ r: 6 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-gray-400">
                  Aucune donnée disponible
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Invoices */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-semibold">
                الفواتير الأخيرة / Factures Récentes
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate({ to: "/apps/sales" })}
              >
                <span className="text-sm">Voir tout</span>
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {recentInvoices && recentInvoices.length > 0 ? (
                  recentInvoices.map((invoice) => (
                    <div
                      key={invoice.id}
                      className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg cursor-pointer transition-colors"
                      onClick={() =>
                        navigate({ to: `/invoices/${invoice.id}` })
                      }
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900">
                            {invoice.invoice_number}
                          </span>
                          <span
                            className={cn(
                              "px-2 py-0.5 rounded-full text-xs font-medium",
                              statusColors[
                                invoice.status as keyof typeof statusColors
                              ],
                            )}
                          >
                            {
                              statusLabels[
                                invoice.status as keyof typeof statusLabels
                              ]?.split(" / ")[1]
                            }
                          </span>
                        </div>
                        <p className="text-sm text-gray-500 mt-1">
                          {(invoice.clients as any)?.name}
                        </p>
                        <p className="text-xs text-gray-400 mt-0.5">
                          {formatDate(invoice.created_at)}
                        </p>
                      </div>
                      <div className="text-right">
                        <div className="font-semibold text-gray-900">
                          {formatCurrency(invoice.total)}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-gray-400">
                    Aucune facture
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Top Clients */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-semibold">
                أفضل العملاء / Meilleurs Clients
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate({ to: "/apps/clients" })}
              >
                <span className="text-sm">Voir tout</span>
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {topClients && topClients.length > 0 ? (
                  topClients.map((client, index) => (
                    <div
                      key={client.client_id}
                      className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg cursor-pointer transition-colors"
                      onClick={() => navigate({ to: "/apps/clients" })}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-sm">
                          {index + 1}
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">
                            {client.client_name}
                          </div>
                          <div className="text-xs text-gray-500">
                            {client.invoice_count} facture
                            {client.invoice_count > 1 ? "s" : ""}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-semibold text-gray-900">
                          {formatCurrency(client.total_revenue)}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-gray-400">
                    Aucun client
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold">
              إجراءات سريعة / Actions Rapides
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              <Button
                variant="outline"
                className="h-auto flex-col py-4 gap-2"
                onClick={() => navigate({ to: "/apps" })}
              >
                <LayoutGrid className="w-6 h-6 text-indigo-600" />
                <span className="text-sm text-center">Toutes les Apps</span>
              </Button>
              <Button
                variant="outline"
                className="h-auto flex-col py-4 gap-2"
                onClick={() => navigate({ to: "/draft-invoice" })}
              >
                <FileText className="w-6 h-6 text-blue-600" />
                <span className="text-sm">Nouvelle Facture</span>
              </Button>
              <Button
                variant="outline"
                className="h-auto flex-col py-4 gap-2"
                onClick={() => navigate({ to: "/apps/clients" })}
              >
                <Users className="w-6 h-6 text-purple-600" />
                <span className="text-sm">Ajouter Client</span>
              </Button>
              <Button
                variant="outline"
                className="h-auto flex-col py-4 gap-2"
                onClick={() => navigate({ to: "/apps/products" })}
              >
                <Package className="w-6 h-6 text-orange-600" />
                <span className="text-sm">Ajouter Produit</span>
              </Button>
              <Button
                variant="outline"
                className="h-auto flex-col py-4 gap-2"
                onClick={() => navigate({ to: "/apps/reports" })}
              >
                <FileText className="w-6 h-6 text-green-600" />
                <span className="text-sm">Voir Rapports</span>
              </Button>
              <Button
                variant="outline"
                className="h-auto flex-col py-4 gap-2 border-amber-200 hover:bg-amber-50 dark:border-amber-800 dark:hover:bg-amber-900/20"
                onClick={() => navigate({ to: "/settings/subscription" })}
              >
                <CreditCard className="w-6 h-6 text-amber-600" />
                <span className="text-sm">Abonnement</span>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
